import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../utils/axios';
import { format, parseISO, addMonths, subMonths } from 'date-fns';
import { MdChevronLeft, MdChevronRight, MdExpandMore, MdExpandLess } from 'react-icons/md';
import './Budgets.css';

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const fmtMoneyFull = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const todayYm = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const monthRange = (yyyymm) => {
  const [y, m] = yyyymm.split('-');
  const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
  return {
    start_date: `${y}-${m}-01`,
    end_date: `${y}-${m}-${String(lastDay).padStart(2, '0')}`,
  };
};

const EXPENSE_GROUPS = [
  { key: 'fixed', label: 'Fixed' },
  { key: 'flexible', label: 'Flexible' },
  { key: 'non_monthly', label: 'Non-Monthly' },
];

const groupKey = (cat) => {
  if (cat.type === 'income') return 'income';
  const g = (cat.budget_group || 'flexible').toLowerCase();
  if (g === 'fixed') return 'fixed';
  if (g === 'non_monthly') return 'non_monthly';
  return 'flexible';
};

const PlannedInput = ({ value, onSave, disabled }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const commit = () => {
    const n = parseFloat(draft);
    if (!Number.isNaN(n) && n !== parseFloat(value || 0)) {
      onSave(n);
    } else if (draft === '' && value) {
      onSave(0);
    }
  };

  return (
    <input
      type="number"
      step="0.01"
      min="0"
      className="planned-input"
      value={draft}
      placeholder="$0"
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
};

const Row = ({ category, planned, actual, onSavePlanned, isIncome }) => {
  const remaining = (planned || 0) - (actual || 0);
  const overSpent = !isIncome && actual > planned && planned > 0;
  return (
    <div className="b-row">
      <div className="b-row-cat">
        <span className="b-cat-icon" style={{ color: category.color }}>{category.icon || '•'}</span>
        <span className="b-cat-name">{category.name}</span>
      </div>
      <div className="b-row-bar">
        {planned > 0 && (
          <div className="b-bar">
            <div
              className={`b-bar-fill ${overSpent ? 'over' : ''}`}
              style={{ width: `${Math.min(100, (actual / planned) * 100)}%` }}
            />
          </div>
        )}
      </div>
      <div className="b-row-planned">
        <PlannedInput
          value={planned ? planned.toFixed(0) : ''}
          onSave={onSavePlanned}
        />
      </div>
      <div className="b-row-actual">{fmtMoneyFull(actual || 0)}</div>
      {isIncome ? (
        <div className="b-row-remaining"></div>
      ) : (
        <div className={`b-row-remaining ${remaining < 0 ? 'neg' : remaining > 0 ? 'pos' : ''}`}>
          {planned === 0 && actual === 0 ? '—' : fmtMoneyFull(remaining)}
        </div>
      )}
    </div>
  );
};

const Budgets = () => {
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(todayYm());
  const [message, setMessage] = useState({ type: '', text: '' });
  const [collapsed, setCollapsed] = useState({});

  const fetchAll = useCallback(async () => {
    const range = monthRange(selectedMonth);
    try {
      const [catRes, budgetRes, summaryRes] = await Promise.all([
        api.get('/api/categories'),
        // Always pass month so budgets keyed (category, month) round-trip
        // correctly when the user is viewing a non-current month.
        api.get('/api/budgets/current', { params: { ...range, month: selectedMonth } }),
        api.get('/api/transactions/summary', { params: range }),
      ]);
      setCategories(catRes.data);
      setBudgets(budgetRes.data);
      setSummary(summaryRes.data);
    } catch (e) {
      console.error('Budgets fetch failed', e);
      setMessage({ type: 'error', text: 'Failed to load budget data' });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const budgetByCategoryId = useMemo(() => {
    const map = new Map();
    budgets.forEach((b) => map.set(b.category_id, b));
    return map;
  }, [budgets]);

  const summaryByCategoryId = useMemo(() => {
    const map = new Map();
    summary.forEach((s) => map.set(s.category_id, s));
    return map;
  }, [summary]);

  const handleSavePlanned = async (category, amount) => {
    const existing = budgetByCategoryId.get(category.id);
    try {
      if (existing) {
        await api.put(`/api/budgets/${existing.id}`, { amount });
      } else if (amount > 0) {
        // Tag the budget to the month the user is currently viewing —
        // otherwise the backend defaults to today's calendar month and the
        // new budget vanishes from the view.
        await api.post('/api/budgets', {
          category_id: category.id,
          amount,
          month: selectedMonth,
        });
      }
      fetchAll();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Failed to save' });
    }
  };

  // Bucketize categories. Transfer category is excluded from budgeting entirely —
  // transfers between your own accounts shouldn't be budgeted as income or expense.
  const byGroup = useMemo(() => {
    const groups = { income: [], fixed: [], flexible: [], non_monthly: [] };
    categories
      .filter((c) => c.type !== 'transfer')
      .forEach((c) => groups[groupKey(c)].push(c));
    return groups;
  }, [categories]);

  // Aggregate totals per group
  const totals = useMemo(() => {
    const computeFor = (cats, kind /* 'income' | 'expense' */) =>
      cats.reduce(
        (acc, c) => {
          const planned = parseFloat(budgetByCategoryId.get(c.id)?.amount || 0);
          const s = summaryByCategoryId.get(c.id);
          const actual = kind === 'income'
            ? parseFloat(s?.total_income || 0)
            : parseFloat(s?.total_expenses || 0);
          acc.planned += planned;
          acc.actual += actual;
          return acc;
        },
        { planned: 0, actual: 0 }
      );

    return {
      income: computeFor(byGroup.income, 'income'),
      fixed: computeFor(byGroup.fixed, 'expense'),
      flexible: computeFor(byGroup.flexible, 'expense'),
      non_monthly: computeFor(byGroup.non_monthly, 'expense'),
    };
  }, [byGroup, budgetByCategoryId, summaryByCategoryId]);

  const totalIncomeActual = totals.income.actual;
  const totalIncomePlanned = totals.income.planned;
  const totalExpensesPlanned = totals.fixed.planned + totals.flexible.planned + totals.non_monthly.planned;
  const leftToBudget = totalIncomePlanned - totalExpensesPlanned;

  if (loading) return <div className="loading">Loading budget...</div>;

  const monthDate = parseISO(`${selectedMonth}-01`);

  return (
    <div className="budget-page">
      {message.text && <div className={message.type}>{message.text}</div>}

      <div className="budget-header">
        <h2 className="budget-title">{format(monthDate, 'MMMM yyyy')}</h2>
        <div className="month-nav">
          <button
            className="month-nav-btn"
            onClick={() => {
              const prev = subMonths(monthDate, 1);
              setSelectedMonth(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`);
            }}
            title="Previous month"
          >
            <MdChevronLeft />
          </button>
          <button
            className="month-nav-btn today"
            onClick={() => setSelectedMonth(todayYm())}
          >
            Today
          </button>
          <button
            className="month-nav-btn"
            onClick={() => {
              const next = addMonths(monthDate, 1);
              setSelectedMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`);
            }}
            title="Next month"
          >
            <MdChevronRight />
          </button>
        </div>
      </div>

      <div className="budget-body">
        <div className="budget-tables">
          {/* INCOME */}
          <div className="card budget-section">
            <div className="b-column-head income">
              <button
                className="b-section-toggle"
                onClick={() => setCollapsed({ ...collapsed, income: !collapsed.income })}
              >
                {collapsed.income ? <MdExpandMore /> : <MdExpandLess />}
                <span>Income</span>
              </button>
              <span></span>
              <span className="b-col-planned">Planned</span>
              <span className="b-col-actual">Actual</span>
              <span></span>
            </div>

            {!collapsed.income && (
              <>
                <div className="b-group">
                  <div className="b-group-head">
                    <button
                      className="b-section-toggle small"
                      onClick={() => setCollapsed({ ...collapsed, income_g: !collapsed.income_g })}
                    >
                      {collapsed.income_g ? <MdExpandMore /> : <MdExpandLess />}
                      <span>Income</span>
                    </button>
                    <span className="b-group-bar">
                      <span
                        className="b-bar-mini"
                        style={{
                          width: totals.income.planned > 0
                            ? `${Math.min(100, (totals.income.actual / totals.income.planned) * 100)}%`
                            : '0%',
                        }}
                      />
                    </span>
                    <span className="b-row-planned">{fmtMoney(totals.income.planned)}</span>
                    <span className="b-row-actual">{fmtMoney(totals.income.actual)}</span>
                    <span></span>
                  </div>
                  {!collapsed.income_g && byGroup.income.map((c) => {
                    const b = budgetByCategoryId.get(c.id);
                    const s = summaryByCategoryId.get(c.id);
                    return (
                      <Row
                        key={c.id}
                        category={c}
                        planned={parseFloat(b?.amount || 0)}
                        actual={parseFloat(s?.total_income || 0)}
                        onSavePlanned={(v) => handleSavePlanned(c, v)}
                        isIncome
                      />
                    );
                  })}
                </div>

                <div className="b-totals-row">
                  <span className="b-totals-label">Total Income</span>
                  <span></span>
                  <span className="b-row-planned strong">{fmtMoney(totalIncomePlanned)}</span>
                  <span className="b-row-actual strong">{fmtMoney(totalIncomeActual)}</span>
                  <span></span>
                </div>
              </>
            )}
          </div>

          {/* EXPENSES */}
          <div className="card budget-section">
            <div className="b-column-head expense">
              <button
                className="b-section-toggle"
                onClick={() => setCollapsed({ ...collapsed, expenses: !collapsed.expenses })}
              >
                {collapsed.expenses ? <MdExpandMore /> : <MdExpandLess />}
                <span>Expenses</span>
              </button>
              <span></span>
              <span className="b-col-planned">Planned</span>
              <span className="b-col-actual">Actual</span>
              <span className="b-col-remaining">Remaining</span>
            </div>

            {!collapsed.expenses && EXPENSE_GROUPS.map(({ key, label }) => {
              const cats = byGroup[key];
              if (cats.length === 0) return null;
              const grouped = collapsed[`exp_${key}`];
              const t = totals[key];
              const groupRemaining = t.planned - t.actual;
              return (
                <div key={key} className="b-group">
                  <div className="b-group-head">
                    <button
                      className="b-section-toggle small"
                      onClick={() => setCollapsed({ ...collapsed, [`exp_${key}`]: !grouped })}
                    >
                      {grouped ? <MdExpandMore /> : <MdExpandLess />}
                      <span>{label}</span>
                    </button>
                    <span className="b-group-bar">
                      <span
                        className={`b-bar-mini ${t.actual > t.planned && t.planned > 0 ? 'over' : ''}`}
                        style={{
                          width: t.planned > 0
                            ? `${Math.min(100, (t.actual / t.planned) * 100)}%`
                            : '0%',
                        }}
                      />
                    </span>
                    <span className="b-row-planned">{fmtMoney(t.planned)}</span>
                    <span className="b-row-actual">{fmtMoney(t.actual)}</span>
                    <span className={`b-row-remaining ${groupRemaining >= 0 ? 'pos' : 'neg'}`}>
                      {fmtMoney(groupRemaining)}
                    </span>
                  </div>
                  {!grouped && cats.map((c) => {
                    const b = budgetByCategoryId.get(c.id);
                    const s = summaryByCategoryId.get(c.id);
                    return (
                      <Row
                        key={c.id}
                        category={c}
                        planned={parseFloat(b?.amount || 0)}
                        actual={parseFloat(s?.total_expenses || 0)}
                        onSavePlanned={(v) => handleSavePlanned(c, v)}
                      />
                    );
                  })}
                </div>
              );
            })}

            <div className="b-totals-row">
              <span className="b-totals-label">Total Expenses</span>
              <span></span>
              <span className="b-row-planned strong">{fmtMoney(totalExpensesPlanned)}</span>
              <span className="b-row-actual strong">
                {fmtMoney(totals.fixed.actual + totals.flexible.actual + totals.non_monthly.actual)}
              </span>
              <span className={`b-row-remaining strong ${
                totalExpensesPlanned - (totals.fixed.actual + totals.flexible.actual + totals.non_monthly.actual) >= 0 ? 'pos' : 'neg'
              }`}>
                {fmtMoney(
                  totalExpensesPlanned - (totals.fixed.actual + totals.flexible.actual + totals.non_monthly.actual)
                )}
              </span>
            </div>
          </div>
        </div>

        {/* RIGHT SUMMARY */}
        <aside className="budget-summary">
          <div className={`budget-summary-headline ${leftToBudget < 0 ? 'neg' : 'pos'}`}>
            <div className="headline-value">{fmtMoney(leftToBudget)}</div>
            <div className="headline-label">Left to budget</div>
          </div>

          <div className="summary-card card">
            {EXPENSE_GROUPS.map(({ key, label }) => {
              const t = totals[key];
              const rem = t.planned - t.actual;
              const pct = t.planned > 0 ? Math.min(100, (t.actual / t.planned) * 100) : 0;
              return (
                <div key={key} className="summary-group">
                  <div className="summary-group-head">
                    <span className="summary-group-label">{label}</span>
                    <span className="summary-group-planned">{fmtMoney(t.planned)} planned</span>
                  </div>
                  <div className="summary-group-bar">
                    <div
                      className={`summary-group-fill ${t.actual > t.planned && t.planned > 0 ? 'over' : ''}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="summary-group-foot">
                    <span>{fmtMoneyFull(t.actual)} spent</span>
                    <span className={rem < 0 ? 'neg' : ''}>
                      {fmtMoneyFull(Math.abs(rem))} {rem < 0 ? 'over' : 'remaining'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Budgets;
